import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import useGameTeamsQuery from '../hooks/useGameTeamsQuery';
import { useGameDates } from '../hooks/gameQueries';
import { getTeamLogoUrl, teamLogoSize } from '@/shared/services/getTeamLogoUrl';
import { useUserStateContext } from '@/shared/components/UserStateProvider';
import { TeamModel } from '@/features/teams/types/TeamModel';
import { teamAutocompleteFilter } from '@/features/teams/functions';
import ListSeasons from './ListSeasons';
import useListSeasonsQuery from '../hooks/useListSeasonsQuery';
import {
  pickSnapDate,
  seasonDayKey,
  localDayKey,
  dateFromDayKey,
} from '../services/seasonDates';

// Drop-in compatible with GamesForm
export type FormValues = {
  date?: Date;
  team?: string;
  streamType: string[];
  seasonId: string;
  leagueId?: string;
  orgId?: string;
  available: boolean;
};

export const DEFAULT_VALUES: FormValues = {
  date: undefined,
  team: undefined,
  streamType: ['live', 'recorded', 'scheduled'],
  seasonId: ' ',
  leagueId: undefined,
  orgId: undefined,
  available: true,
};

type Props = {
  onChange: (values: FormValues) => unknown;
  showAvailableToggle?: boolean;
  defaultDate?: Date;
};

// ─── Date utilities ───────────────────────────────────────────────────────────

const startOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

// Parse a date value from the API (ISO string or Date) as a local calendar date,
// ignoring the UTC time component. "2026-04-16T00:00:00.000Z" → April 16 local midnight.
// Returns null for an absent or unparseable value — `endDate` is nullable.
const parseLocalDate = (d: Date | string | null | undefined): Date | null =>
  dateFromDayKey(seasonDayKey(d));

const addDays = (date: Date, n: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const daysBetween = (a: Date, b: Date): number =>
  Math.round((b.getTime() - a.getTime()) / 86_400_000);

const clampDate = (date: Date, lower: Date | null, upper: Date | null): Date => {
  if (lower && date < lower) return new Date(lower);
  if (upper && date > upper) return new Date(upper);
  return date;
};

const DAY_ABBR  = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON_ABBR  = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                   'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_CARD_WIDTH   = 72;  // px — determines how many cards fit
const BUFFER           = 14;  // extra days rendered on each side for smooth animation
const ANIM_DURATION_MS = 280;

// A game that has been *played* is a recorded one — live and scheduled games
// have not happened yet. Module-level so the query key stays stable.
const PLAYED_STREAM_TYPES = ['recorded'];

// ─── Component ────────────────────────────────────────────────────────────────

const GamesFormV2 = ({ onChange, defaultDate }: Props) => {
  const initialDate = defaultDate ? startOfDay(defaultDate) : undefined;
  const methods = useForm<FormValues>({ defaultValues: { ...DEFAULT_VALUES, date: initialDate } });
  const { watch, control, register, setValue, getValues } = methods;

  const { currentLeague, currentOrg } = useUserStateContext();

  // ── Filter state ──
  const [currentSeason, setCurrentSeason] = useState(DEFAULT_VALUES.seasonId);
  const seasonIdForQuery =
    currentSeason && currentSeason !== ' ' ? parseInt(currentSeason) : undefined;
  const teamsQuery = useGameTeamsQuery(currentLeague?.id, seasonIdForQuery);

  const listSeasonsQuery = useListSeasonsQuery(currentLeague?.id ?? 0);
  const selectedSeason = useMemo(() => {
    if (!currentSeason || currentSeason === ' ') return undefined;
    return listSeasonsQuery.data?.seasons.find((s) => s.id.toString() === currentSeason);
  }, [currentSeason, listSeasonsQuery.data]);

  // A null on either side means "unbounded there", not "bounded at the epoch":
  // endDate is nullable, and mapping it through new Date(null) used to yield
  // 1970-01-01, which put every day out of bounds and froze the next arrow.
  const seasonBounds = useMemo(
    () => ({
      lower: parseLocalDate(selectedSeason?.startDate),
      upper: parseLocalDate(selectedSeason?.endDate),
    }),
    [selectedSeason],
  );

  // ── Game dates query (for greying out days with no games) ──
  const watchedTeam = watch('team');
  const watchedStreamTypes = watch('streamType');
  const gameDatesQuery = useGameDates({
    seasonId: seasonIdForQuery,
    teamId: watchedTeam ? Number(watchedTeam) : undefined,
    streamTypes: watchedStreamTypes,
    orgId: currentOrg?.id,
    leagueId: currentLeague?.id,
  });
  const gameDateSet = useMemo(() => {
    if (!gameDatesQuery.data?.dates) return null;
    return new Set(gameDatesQuery.data.dates);
  }, [gameDatesQuery.data]);

  // ── Last game PLAYED in the season (drives the season-change date snap) ──
  // Deliberately its own query rather than reusing gameDatesQuery above: that
  // one follows the user's stream-type filter (live/scheduled included) and is
  // only used to grey out empty days. No teamId — the question is when the
  // SEASON last played, not when a particular team did.
  const playedDatesQuery = useGameDates({
    seasonId: seasonIdForQuery,
    streamTypes: PLAYED_STREAM_TYPES,
    orgId: currentOrg?.id,
    leagueId: currentLeague?.id,
  });

  // ── Date strip state ──
  const today = useMemo(() => startOfDay(new Date()), []);
  const [numVisible, setNumVisible]     = useState(7);
  const [containerWidth, setContainerWidth] = useState(0);

  // windowStart: the leftmost VISIBLE day in the strip
  const [windowStart, setWindowStart]   = useState<Date>(() =>
    addDays(initialDate ?? today, -Math.floor(7 / 2)),
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);

  // Keep a ref so callbacks/effects can read selectedDate without adding it to deps
  const selectedDateRef = useRef<Date | undefined>(undefined);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);

  // Animation
  const [animOffset, setAnimOffset]     = useState(0);       // extra px offset during animation
  const [transitioning, setTransitioning] = useState(false);
  const animating = useRef(false);

  // Avoid stale closure in setTimeout
  const windowStartRef = useRef(windowStart);
  useEffect(() => { windowStartRef.current = windowStart; }, [windowStart]);

  // Whether the user has manually positioned the strip
  const hasNavigated = useRef(false);

  // ── Container measurement ──
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setContainerWidth(w);
      setNumVisible(Math.max(1, Math.floor(w / MIN_CARD_WIDTH)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-center when numVisible settles (before user navigates), clamped to season bounds
  useEffect(() => {
    if (!hasNavigated.current) {
      const center = clampDate(today, seasonBounds.lower, seasonBounds.upper);
      setWindowStart(addDays(center, -Math.floor(numVisible / 2)));
    }
  }, [numVisible, today, seasonBounds]);

  // On season change, snap the strip to the day worth opening on. The rule
  // itself lives in services/seasonDates so it can be unit-tested; see the
  // comment there for why each branch exists.
  //
  // "Running" is decided by the season's date window, not by `season.is_active`
  // — nothing writes that flag, and the seasons endpoint has already filtered
  // on it, so every row that reaches here has it set.
  //
  // The ref holds the last season snapped (not a set of seen ones), so any
  // change re-snaps — including returning to an earlier season — while a manual
  // day pick within the same season is left alone. That one-shot behaviour is
  // why pickSnapDate returning null must NOT latch: null covers "still
  // loading", and latching it would freeze the strip on the wrong day.
  const snappedSeasonRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!seasonIdForQuery) return;
    if (snappedSeasonRef.current === seasonIdForQuery) return;
    // Wait for the season row: with its bounds still unknown every season looks
    // finished, and we would snap to last-played before the dates arrive.
    if (!selectedSeason) return;

    const target = pickSnapDate({
      todayKey: localDayKey(today),
      startKey: seasonDayKey(selectedSeason.startDate),
      endKey: seasonDayKey(selectedSeason.endDate),
      playedKeys: playedDatesQuery.data?.dates,
      // Deliberately the user-filtered list rather than a third request: it is
      // the same set the strip greys empty days out with, so the fallback
      // always lands on a day the strip itself shows as populated.
      allKeys: gameDatesQuery.data?.dates,
    });
    if (!target) return;

    snappedSeasonRef.current = seasonIdForQuery;
    setSelectedDate(target);
    setValue('date', target);
    // Position the strip directly rather than via animateTo — there is no
    // meaningful origin to slide from when the whole season just changed.
    hasNavigated.current = true;
    setWindowStart(addDays(target, -Math.floor(numVisible / 2)));
  }, [
    seasonIdForQuery,
    selectedSeason,
    today,
    playedDatesQuery.data,
    gameDatesQuery.data,
    numVisible,
    setValue,
  ]);

  // ── Parent notification ──
  const lastNotifiedValues = useRef<string>('');
  const isInitialized      = useRef(false);

  const notifyParent = useCallback(
    (values: FormValues) => {
      const { leagueId, orgId } = values;
      if (!leagueId || !orgId) return;
      const str = JSON.stringify(values);
      if (str === lastNotifiedValues.current) return;
      lastNotifiedValues.current = str;
      const converted = Object.keys(values).reduce((acc: any, key) => {
        const val = values[key as keyof FormValues];
        acc[key] =
          val && typeof val === 'object' && typeof (val as any).toDate === 'function'
            ? (val as any).toDate()
            : val;
        return acc;
      }, {} as FormValues);
      onChange(converted);
    },
    [onChange],
  );

  const prevScopeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (currentLeague?.id && currentOrg?.id) {
      setValue('leagueId', currentLeague.id.toString());
      setValue('orgId', currentOrg.id.toString());
      // Clear the season so ListSeasons re-selects the new scope's default —
      // otherwise queries fire with a stale seasonId. Org is part of the scope:
      // seasons are league-keyed but the games behind them are filtered by the
      // org's access, so switching org has to re-pick the default too.
      // Only on an ACTUAL change, not on (re)mount: a warm remount would wipe a
      // still-valid selection and blank the games list for a beat.
      const scope = `${currentOrg.id}:${currentLeague.id}`;
      if (prevScopeRef.current !== undefined && prevScopeRef.current !== scope) {
        setCurrentSeason(DEFAULT_VALUES.seasonId);
        setValue('seasonId', DEFAULT_VALUES.seasonId);
      }
      prevScopeRef.current = scope;
      teamsQuery.refetch();
      gameDatesQuery.refetch();
    }
  }, [currentLeague, currentOrg, setValue]);

  useEffect(() => {
    if (currentLeague?.id && isInitialized.current) {
      teamsQuery.refetch();
      gameDatesQuery.refetch();
    }
  }, [currentSeason, currentLeague?.id]);

  // The team list is season-scoped, so a team picked in one season can be absent
  // from the next. Drop the stale selection once the new list settles — otherwise
  // the games list filters to nothing behind an already-blank Team box. Mirrors
  // the out-of-bounds date reset in handleSeasonChange.
  // isSuccess is true while placeholderData ([]) is being served, so gate on
  // isPlaceholderData too — otherwise a valid pick is wiped before data lands.
  useEffect(() => {
    if (!teamsQuery.isSuccess || teamsQuery.isFetching || teamsQuery.isPlaceholderData) return;
    const selected = getValues('team');
    if (!selected) return;
    const teams = (teamsQuery.data ?? []) as TeamModel[];
    if (!teams.some((t) => t.id.toString() === selected)) {
      setValue('team', '');
    }
  }, [
    teamsQuery.data,
    teamsQuery.isSuccess,
    teamsQuery.isFetching,
    teamsQuery.isPlaceholderData,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    const sub = watch(() => {
      if (isInitialized.current) notifyParent(getValues());
    });
    return () => sub.unsubscribe();
  }, [watch, getValues, notifyParent]);

  // ── Handlers ──

  const handleSeasonChange = useCallback(
    (seasonId: string) => {
      setCurrentSeason(seasonId);
      setValue('seasonId', seasonId);

      // Reset strip position so it re-centers to the new season's bounds
      hasNavigated.current = false;

      // Clear selected date if it falls outside the new season's bounds
      const newSeason = listSeasonsQuery.data?.seasons.find((s) => s.id.toString() === seasonId);
      if (newSeason && selectedDateRef.current) {
        const lower = parseLocalDate(newSeason.startDate);
        const upper = parseLocalDate(newSeason.endDate);
        const d = startOfDay(selectedDateRef.current);
        // An absent bound constrains nothing — startOfDay(null) is the epoch,
        // which made every date read as out of bounds and cleared the pick.
        const inBounds = (!lower || d >= lower) && (!upper || d <= upper);
        if (!inBounds) {
          setSelectedDate(undefined);
          setValue('date', undefined);
        }
      }

      if (!isInitialized.current && currentLeague?.id && currentOrg?.id) {
        isInitialized.current = true;
        setTimeout(() => notifyParent(getValues()), 0);
      }
    },
    [setValue, currentLeague, currentOrg, getValues, notifyParent, listSeasonsQuery.data],
  );

  const isInBounds = useCallback(
    (day: Date): boolean => {
      const d = startOfDay(day);
      if (seasonBounds.lower && d < startOfDay(seasonBounds.lower)) return false;
      if (seasonBounds.upper && d > startOfDay(seasonBounds.upper)) return false;
      return true;
    },
    [seasonBounds],
  );

  const isPrevDisabled =
    seasonBounds.lower !== null &&
    startOfDay(windowStart) <= startOfDay(seasonBounds.lower);

  const isNextDisabled =
    seasonBounds.upper !== null &&
    startOfDay(addDays(windowStart, numVisible - 1)) >= startOfDay(seasonBounds.upper);

  // Width of one card slot (card + its share of gap). Used for translation math.
  const cardSlotWidth = containerWidth > 0 ? containerWidth / numVisible : MIN_CARD_WIDTH;

  const animateTo = useCallback(
    (targetWindowStart: Date) => {
      if (animating.current) return;
      const daysShift = daysBetween(windowStartRef.current, targetWindowStart);
      if (daysShift === 0) return;

      const pixelShift = -(daysShift * cardSlotWidth);

      animating.current = true;
      setTransitioning(true);
      setAnimOffset(pixelShift);

      setTimeout(() => {
        // Snap: disable transition → update windowStart → reset offset
        // All in one batch so React applies them together without a visual frame gap.
        setTransitioning(false);
        setWindowStart(addDays(windowStartRef.current, daysShift));
        setAnimOffset(0);
        animating.current = false;
      }, ANIM_DURATION_MS);
    },
    [cardSlotWidth],
  );

  const handleDaySelect = useCallback(
    (day: Date) => {
      if (animating.current) return;
      if (!isInBounds(day)) return;
      const toggled = selectedDate && isSameDay(day, selectedDate) ? undefined : day;
      setSelectedDate(toggled);
      setValue('date', toggled);

      if (toggled) {
        // Center the selected day
        const targetStart = addDays(toggled, -Math.floor(numVisible / 2));
        hasNavigated.current = true;
        animateTo(targetStart);
      }
    },
    [selectedDate, setValue, numVisible, animateTo, isInBounds],
  );

  const handlePrev = useCallback(() => {
    if (animating.current) return;
    hasNavigated.current = true;
    animateTo(addDays(windowStartRef.current, -numVisible));
  }, [numVisible, animateTo]);

  const handleNext = useCallback(() => {
    if (animating.current) return;
    hasNavigated.current = true;
    animateTo(addDays(windowStartRef.current, numVisible));
  }, [numVisible, animateTo]);

  // ── Rendered strip ────────────────────────────────────────────────────────

  // Render BUFFER extra days on each side so there are cards to scroll into
  const renderStart  = addDays(windowStart, -BUFFER);
  const totalCards   = numVisible + 2 * BUFFER;
  const renderedDays = useMemo(
    () => Array.from({ length: totalCards }, (_, i) => addDays(renderStart, i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowStart, numVisible],
  );

  // Base translateX hides the left buffer; animOffset adds the slide
  const baseTranslate  = -(BUFFER * cardSlotWidth);
  const totalTranslate = baseTranslate + animOffset;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <FormProvider {...methods}>
      <input value={currentLeague?.id || ''} type="hidden" {...register('leagueId')} />
      <input value={currentOrg?.id || ''} type="hidden" {...register('orgId')} />

      <Box sx={{ pt: 1, pb: 0 }}>

        {/* Row 1 — Season + Team */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>

          <Box sx={{ width: 200 }}>
            {currentLeague?.id && typeof currentLeague.id === 'number' && (
              <ListSeasons
                key={currentLeague.id}
                leagueId={currentLeague.id}
                value={currentSeason}
                onChange={handleSeasonChange}
              />
            )}
          </Box>

          <Box sx={{ width: 200 }}>
            <Controller
              name="team"
              control={control}
              render={({ field }) => {
                const selectedTeam = field.value
                  ? teamsQuery.data?.find((t) => t.id.toString() === field.value)
                  : null;
                return (
                  <Autocomplete
                    size="small"
                    value={selectedTeam || null}
                    onChange={(_e, v: TeamModel | null) => field.onChange(v?.id.toString() || '')}
                    groupBy={(option) => (option.abbreviation?.[0] ?? '#').toUpperCase()}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    getOptionLabel={(option) => option?.abbreviation || ''}
                    filterOptions={teamAutocompleteFilter}
                    options={teamsQuery.data || []}
                    renderOption={(props, option) => (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        <img loading="lazy" style={teamLogoSize(36)} src={getTeamLogoUrl(option)} alt={option.abbreviation} />
                        {option.abbreviation}
                        {option.name && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }} noWrap>
                            {option.name}
                          </Typography>
                        )}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Team"
                        fullWidth
                        // Merge into params.slotProps — do NOT replace it. It carries
                        // input.ref (the Popper's anchor), endAdornment and htmlInput
                        // (getInputProps). Overwriting it leaves the popup unanchored,
                        // so the list never opens even with options loaded.
                        slotProps={{
                          ...params.slotProps,
                          input: {
                            ...params.slotProps.input,
                            startAdornment: selectedTeam ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                <img loading="lazy" style={teamLogoSize(24)} src={getTeamLogoUrl(selectedTeam)} alt={selectedTeam.abbreviation} />
                              </Box>
                            ) : params.slotProps.input.startAdornment,
                          },
                        }}
                      />
                    )}
                  />
                );
              }}
            />
          </Box>

        </Box>

        {/* Row 2 — Responsive day strip with smooth centering */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

          <IconButton onClick={handlePrev} size="small" sx={{ flexShrink: 0 }} disabled={isPrevDisabled}>
            <ChevronLeftIcon />
          </IconButton>

          {/* Clipping container — measured by ResizeObserver */}
          <Box
            ref={containerRef}
            sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}
          >
            {/* Sliding strip */}
            <Box
              sx={{
                display: 'flex',
                transform: `translateX(${totalTranslate}px)`,
                transition: transitioning
                  ? `transform ${ANIM_DURATION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
                  : 'none',
                willChange: 'transform',
              }}
            >
              {renderedDays.map((day, i) => {
                const selected = !!selectedDate && isSameDay(day, selectedDate);
                const isToday  = isSameDay(day, today);
                const inBounds = isInBounds(day);
                const hasGames = !gameDateSet || gameDateSet.has(localDayKey(day));

                return (
                  <Box
                    key={i}
                    onClick={() => handleDaySelect(day)}
                    sx={{
                      width: `${cardSlotWidth}px`,
                      flexShrink: 0,
                      boxSizing: 'border-box',
                      px: 0.25,
                      opacity: !inBounds || (!hasGames && !isToday) ? 0.35 : 1,
                      pointerEvents: inBounds && (hasGames || isToday) ? 'auto' : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        border: selected ? '2px solid' : '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 1.5,
                        py: 1.25,
                        cursor: inBounds ? 'pointer' : 'default',
                        userSelect: 'none',
                        textAlign: 'center',
                        bgcolor: selected ? 'action.selected' : 'transparent',
                        transition: 'border-color 0.15s, background-color 0.15s',
                        '&:hover': {
                          borderColor: selected ? 'primary.main' : 'primary.light',
                          bgcolor: selected ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ lineHeight: 1.3, letterSpacing: '0.05em' }}
                      >
                        {DAY_ABBR[day.getDay()]}
                      </Typography>
                      <Typography
                        variant="body2"
                        display="block"
                        fontWeight={isToday ? 700 : 400}
                        color={isToday ? 'primary.main' : 'text.primary'}
                        sx={{ lineHeight: 1.4, whiteSpace: 'nowrap' }}
                      >
                        {MON_ABBR[day.getMonth()]} {day.getDate()}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <IconButton onClick={handleNext} size="small" sx={{ flexShrink: 0 }} disabled={isNextDisabled}>
            <ChevronRightIcon />
          </IconButton>

        </Box>

      </Box>
    </FormProvider>
  );
};

export default GamesFormV2;
